import { useMutation, useQuery } from '@tanstack/react-query';
import { portalGet, portalPost } from '@/lib/portal/customerClient';
import type { RecurringPlanPrice } from '@/lib/portal/recurringPlan';
import type { PortalVisit } from '@/hooks/portal/usePortalVisits';
import { usePortalAuthStore } from '@/stores/portalAuth.store';
import type { RecurringConfig } from '@/types/job';

/** One of the signed-in customer's recurring services. */
export interface PortalRecurringPlan {
  jobId: string;
  jobNumber: string;
  name: string;
  status: string;
  cadence: RecurringConfig | null;
  startDate: string | null;
  endDate: string | null;
  /** The plan's next booked visit, as Service history lists it. */
  nextVisit: PortalVisit | null;
  /** The plan's own price; null for a per-visit series with none. */
  price: RecurringPlanPrice | null;
  minimumTermMonths: number | null;
  cancellationFee: number | null;
  inMinimumTerm: boolean;
}

/** The signed-in customer's recurring services. */
export function usePortalRecurringPlans() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'recurring-plans', token],
    queryFn: () => portalGet<{ plans: PortalRecurringPlan[] }>('/api/portal/recurring-plans'),
    enabled: Boolean(token),
    retry: false,
  });
}

/**
 * Open the recurring self-service page (skip, pause, cancel, auto-renew, the
 * card on file) through a link minted for this customer on demand.
 */
export function useManageRecurringPlans() {
  return useMutation({
    mutationFn: () => portalPost<{ path: string }>('/api/portal/recurring-plans/manage-link'),
    onSuccess: ({ path }) => {
      window.location.assign(path);
    },
  });
}
